import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    createPublicDiscoveryBooking,
    getPublicDiscoveryAvailability,
    getPublicDiscoveryMonthAvailability,
} from "./api";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import "./PortalUI.css";
import "./ReviewPage.css";
import {
    buildBookingRedirectUrl,
    captureTrackingParamsForBooking,
    isDevelopmentEnvironment,
    readTrackingParamsFromStorage,
} from "./discoveryTracking.mjs";

const HELP_OPTIONS = [
    { value: "launch_new", label: "Launching a new podcast" },
    { value: "improve_existing", label: "Improving an existing podcast" },
    { value: "something_else", label: "Something else" },
];

const RELEASE_OPTIONS = [
    { value: "weekly", label: "Weekly" },
    { value: "fortnightly", label: "Fortnightly" },
    { value: "monthly", label: "Monthly" },
    { value: "not_sure", label: "Not sure" },
];

const BUDGET_OPTIONS = [
    { value: "200_300", label: "£200 - £300" },
    { value: "300_500", label: "£300 - £500" },
    { value: "500_1000", label: "£500 - £1,000" },
    { value: "over_1000", label: "Over £1,000" },
];

const initialForm = {
    name: "",
    email: "",
    phone: "",
    podcast_type: "",
    help_with: "",
    release_frequency: "",
    monthly_budget: "",
};
const PHONE_ALLOWED_CHARS_REGEX = /^[+\d\s().-]+$/;
const devLog = (...args) => {
    if (!isDevelopmentEnvironment()) return;
    console.debug(...args);
};

function isValidPhoneNumber(value) {
    const text = String(value || "").trim();
    if (!text) return false;
    if (!PHONE_ALLOWED_CHARS_REGEX.test(text)) return false;
    const digits = text.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
}

function formatDateTime(value, timezone = "UTC") {
    if (!value) return "";
    const raw = String(value).trim();
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
    const text = raw.replace(" ", "T");
    const dt = hasTimezone ? new Date(text) : new Date(`${text}Z`);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
    });
}

function toYmdUtc(dateObj) {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function parseYmdToUtcDate(value) {
    const raw = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const dt = new Date(`${raw}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
}

function monthKeyFromDate(dateObj) {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

function formatMonthLabel(dateObj) {
    return dateObj.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function formatSelectedDate(value) {
    const dt = parseYmdToUtcDate(value);
    if (!dt) return "";
    return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function BookDiscoveryPage() {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(initialForm);
    const [date, setDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [timesLoading, setTimesLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [availability, setAvailability] = useState(null);
    const [submittedSummary, setSubmittedSummary] = useState("");
    const [bookingUiReady, setBookingUiReady] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    });
    const currentMonthStart = useMemo(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    }, []);
    const [monthAvailability, setMonthAvailability] = useState({});
    const monthAvailabilityLoadRef = useRef(0);
    const skipNextDateLoadRef = useRef(false);

    useEffect(() => {
        captureTrackingParamsForBooking({
            locationLike: window.location,
            storageLike: window.sessionStorage,
            log: devLog,
        });
    }, []);

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(form.email || "");
    const phoneValid = isValidPhoneNumber(form.phone || "");
    const detailsValid = useMemo(
        () =>
            Boolean(form.name.trim()) &&
            emailValid &&
            phoneValid &&
            Boolean(form.podcast_type) &&
            Boolean(form.help_with) &&
            Boolean(form.release_frequency) &&
            Boolean(form.monthly_budget),
        [form, emailValid, phoneValid]
    );
    const slots = availability?.available_slots || [];
    const selectedDateLabel = formatSelectedDate(date);
    const selectedDateTimeLabel =
        selectedDateLabel && selectedTime
            ? `${selectedDateLabel}, ${selectedTime}`
            : selectedDateLabel || "";

    async function loadAvailability(dateValue, { allowAutoPickDate = false } = {}) {
        setTimesLoading(true);
        setError("");
        try {
            const res = await getPublicDiscoveryAvailability(dateValue);
            if (!res?.ok) {
                setError(res?.message || "Failed to load available times");
                return;
            }
            setAvailability(res);
            setSelectedTime("");
            if (
                allowAutoPickDate &&
                !dateValue &&
                res?.selected_date &&
                res.selected_date !== date
            ) {
                skipNextDateLoadRef.current = true;
                setDate(res.selected_date);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load available times");
        } finally {
            setTimesLoading(false);
        }
    }

    useEffect(() => {
        if (step !== 1) return;
        if (!date) return;
        if (skipNextDateLoadRef.current) {
            skipNextDateLoadRef.current = false;
            return;
        }
        loadAvailability(date, { allowAutoPickDate: false });
    }, [date, step]);

    useEffect(() => {
        if (step !== 1) return;
        if (availability || timesLoading) return;
        loadAvailability("", { allowAutoPickDate: true });
    }, [step, availability, timesLoading]);

    useEffect(() => {
        const selectedDate = parseYmdToUtcDate(date);
        if (!selectedDate) return;
        const selectedMonth = new Date(
            Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1)
        );
        setCalendarMonth((prev) =>
            monthKeyFromDate(prev) === monthKeyFromDate(selectedMonth) ? prev : selectedMonth
        );
    }, [date]);

    useEffect(() => {
        if (step !== 1 || !calendarMonth) return;
        const key = monthKeyFromDate(calendarMonth);
        const existing = monthAvailability[key];
        if (existing && (existing.loading || existing.loaded)) return;

        const loadId = monthAvailabilityLoadRef.current + 1;
        monthAvailabilityLoadRef.current = loadId;
        setMonthAvailability((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                loading: true,
                loaded: false,
                dates: prev[key]?.dates || {},
            },
        }));

        const run = async () => {
            const res = await getPublicDiscoveryMonthAvailability(key);
            if (monthAvailabilityLoadRef.current !== loadId) return;
            setMonthAvailability((prev) => ({
                ...prev,
                [key]: {
                    loading: false,
                    loaded: true,
                    dates:
                        res?.ok && res?.dates && typeof res.dates === "object"
                            ? res.dates
                            : {},
                },
            }));
        };

        run().catch(() => {
            if (monthAvailabilityLoadRef.current !== loadId) return;
            setMonthAvailability((prev) => ({
                ...prev,
                [key]: { loading: false, loaded: true, dates: {} },
            }));
        });
    }, [step, calendarMonth, monthAvailability]);

    const monthKey = monthKeyFromDate(calendarMonth);
    const canGoToPreviousMonth = calendarMonth.getTime() > currentMonthStart.getTime();
    const monthInfo = monthAvailability[monthKey] || {
        loading: false,
        loaded: false,
        dates: {},
    };
    const monthDates = monthInfo.dates || {};
    const monthFirstWeekdaySunFirst = new Date(
        Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth(), 1)
    ).getUTCDay();
    const monthLastDay = new Date(
        Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth() + 1, 0)
    ).getUTCDate();
    const calendarCells = [];
    for (let i = 0; i < monthFirstWeekdaySunFirst; i += 1) {
        calendarCells.push({ kind: "pad", key: `pad-${i}` });
    }
    for (let d = 1; d <= monthLastDay; d += 1) {
        const ymd = toYmdUtc(
            new Date(Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth(), d))
        );
        const available = monthDates[ymd] === true;
        const selected = ymd === date;
        calendarCells.push({
            kind: "day",
            key: ymd,
            ymd,
            day: d,
            available,
            selected,
        });
    }

    useEffect(() => {
        if (bookingUiReady) return;
        if (step !== 1) return;
        if (!availability) return;
        if (timesLoading) return;
        if (!monthInfo.loaded || monthInfo.loading) return;
        setBookingUiReady(true);
    }, [bookingUiReady, step, availability, timesLoading, monthInfo.loaded, monthInfo.loading]);

    function handleNext() {
        setStep(2);
    }

    async function handleBook() {
        if (!selectedTime || !date) return;
        setSubmitting(true);
        setError("");
        try {
            const payload = {
                ...form,
                date,
                time: selectedTime,
            };
            const res = await createPublicDiscoveryBooking(payload);
            if (!res?.ok) {
                setError(res?.message || "Failed to book discovery call");
                return;
            }

            const trackingParams = readTrackingParamsFromStorage(window.sessionStorage);
            const redirectUrl = buildBookingRedirectUrl({
                baseUrl: availability?.settings?.booking_redirect_url,
                includeParams: Boolean(
                    availability?.settings?.booking_redirect_include_params
                ),
                email: form.email,
                name: form.name,
                trackingParams,
                log: devLog,
            });
            if (redirectUrl) {
                window.location.assign(redirectUrl);
                return;
            }

            setSubmittedSummary(
                formatDateTime(
                    res?.discovery_call_scheduled_at,
                    availability?.settings?.timezone || "Europe/London"
                )
            );
        } catch (err) {
            console.error(err);
            setError("Failed to book discovery call");
        } finally {
            setSubmitting(false);
        }
    }

    if (submittedSummary) {
        return (
            <div className="review-page">
                <div className="review-brand">
                    <picture>
                        <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                        <img
                            src={logoDark}
                            alt="Urban Podcasts"
                            className="review-brand__logo"
                        />
                    </picture>
                </div>
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">Thanks for booking</h1>
                    <p>Your discovery call is set for {submittedSummary}.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="review-page">
            <div className="review-brand">
                <picture>
                    <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                    <img
                        src={logoDark}
                        alt="Urban Podcasts"
                        className="review-brand__logo"
                    />
                </picture>
            </div>
            <div className="review-card card">
                <h1 className="review-title">Book a Discovery Call</h1>
                <p className="review-subtitle">
                    {step === 1
                        ? "Step 1 of 2: Pick your preferred date and time."
                        : "Step 2 of 2: Tell us about you and your podcast goals."}
                </p>

                {error && <div className="alert alert--error">{error}</div>}

                {step === 1 ? (
                    <div className="discovery-booking-layout">
                        {!bookingUiReady ? (
                            <p>Loading available dates and times…</p>
                        ) : (
                            <>
                        <div className="discovery-booking-summary-wrap">
                            <div className="discovery-booking-summary-time">
                                {selectedDateTimeLabel || "Select date and time"}
                            </div>
                        </div>

                        <div className="discovery-booking-grid">
                            <div className="discovery-booking-col">
                                <div className="discovery-booking-col__title">Date</div>
                                <div className="discovery-booking-month-nav">
                                    <button
                                        type="button"
                                        className="btn btn--secondary btn--small"
                                        onClick={() => {
                                            if (!canGoToPreviousMonth) return;
                                            setCalendarMonth(
                                                new Date(
                                                    Date.UTC(
                                                        calendarMonth.getUTCFullYear(),
                                                        calendarMonth.getUTCMonth() - 1,
                                                        1
                                                    )
                                                )
                                            );
                                        }}
                                        disabled={timesLoading || !canGoToPreviousMonth}
                                    >
                                        Prev
                                    </button>
                                    <span className="discovery-booking-month-label">
                                        {formatMonthLabel(calendarMonth)}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn--secondary btn--small"
                                        onClick={() =>
                                            setCalendarMonth(
                                                new Date(
                                                    Date.UTC(
                                                        calendarMonth.getUTCFullYear(),
                                                        calendarMonth.getUTCMonth() + 1,
                                                        1
                                                    )
                                                )
                                            )
                                        }
                                        disabled={timesLoading}
                                    >
                                        Next
                                    </button>
                                </div>
                                <div className="discovery-booking-weekdays">
                                    {["S", "M", "T", "W", "T", "F", "S"].map((label, idx) => (
                                        <div
                                            key={`${label}-${idx}`}
                                            className="discovery-booking-weekdays__day"
                                        >
                                            {label}
                                        </div>
                                    ))}
                                </div>
                                <div className="discovery-booking-days">
                                    {calendarCells.map((cell) => {
                                        if (cell.kind === "pad") {
                                            return <div key={cell.key} />;
                                        }
                                        const isUnavailable = !cell.available;
                                        return (
                                            <button
                                                key={cell.key}
                                                type="button"
                                                onClick={() => setDate(cell.ymd)}
                                                disabled={isUnavailable || timesLoading}
                                                className={
                                                    "btn btn--small discovery-booking-day " +
                                                    (cell.selected ? "btn--primary" : "btn--secondary") +
                                                    (isUnavailable ? " discovery-booking-day--unavailable" : "")
                                                }
                                            >
                                                {cell.day}
                                            </button>
                                        );
                                    })}
                                </div>
                                {monthInfo.loading ? (
                                    <p className="text-description" style={{ marginTop: 8 }}>
                                        Checking available dates...
                                    </p>
                                ) : null}
                            </div>

                            <div className="discovery-booking-col">
                                <div className="discovery-booking-col__title">Time</div>
                                <div className="discovery-slots-grid">
                                    {timesLoading || !availability ? (
                                        <p
                                            style={{
                                                gridColumn: "1 / -1",
                                                textAlign: "center",
                                                margin: "8px 0",
                                            }}
                                        >
                                            Loading available times…
                                        </p>
                                    ) : slots.length === 0 ? (
                                        <p
                                            style={{
                                                gridColumn: "1 / -1",
                                                textAlign: "center",
                                                margin: "8px 0",
                                            }}
                                        >
                                            No time slots available for this date.
                                        </p>
                                    ) : (
                                        slots.map((slot) => (
                                            <button
                                                key={slot}
                                                type="button"
                                                className={
                                                    "btn discovery-slot-btn " +
                                                    (selectedTime === slot
                                                        ? "btn--primary"
                                                        : "btn--secondary")
                                                }
                                                disabled={timesLoading}
                                                onClick={() => setSelectedTime(slot)}
                                            >
                                                {slot}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className="btn btn--primary"
                                disabled={!selectedTime || timesLoading}
                                onClick={handleNext}
                            >
                                Next
                            </button>
                        </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{ marginTop: 12 }}>
                        <label className="form-field">
                            <span className="form-field__label">Name</span>
                            <input
                                className="form-field__input"
                                value={form.name}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                                required
                            />
                        </label>

                        <label className="form-field">
                            <span className="form-field__label">Email</span>
                            <input
                                type="email"
                                className="form-field__input"
                                value={form.email}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, email: e.target.value }))
                                }
                                required
                            />
                        </label>

                        <label className="form-field">
                            <span className="form-field__label">Phone</span>
                            <input
                                className="form-field__input"
                                value={form.phone}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, phone: e.target.value }))
                                }
                                required
                            />
                        </label>

                        <label className="form-field">
                            <span className="form-field__label">Business or personal</span>
                            <select
                                className="form-field__select"
                                value={form.podcast_type}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        podcast_type: e.target.value,
                                    }))
                                }
                                required
                            >
                                <option value="">Select…</option>
                                <option value="business">Business</option>
                                <option value="personal">Personal</option>
                            </select>
                        </label>

                        <label className="form-field">
                            <span className="form-field__label">
                                What do you need help with?
                            </span>
                            <select
                                className="form-field__select"
                                value={form.help_with}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, help_with: e.target.value }))
                                }
                                required
                            >
                                <option value="">Select…</option>
                                {HELP_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="form-field">
                            <span className="form-field__label">
                                What&apos;s your release frequency?
                            </span>
                            <select
                                className="form-field__select"
                                value={form.release_frequency}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        release_frequency: e.target.value,
                                    }))
                                }
                                required
                            >
                                <option value="">Select…</option>
                                {RELEASE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="form-field">
                            <span className="form-field__label">
                                What&apos;s your monthly budget?
                            </span>
                            <select
                                className="form-field__select"
                                value={form.monthly_budget}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        monthly_budget: e.target.value,
                                    }))
                                }
                                required
                            >
                                <option value="">Select…</option>
                                {BUDGET_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {!emailValid && form.email ? (
                            <div className="alert alert--error">
                                Please enter a valid email address.
                            </div>
                        ) : null}
                        {!phoneValid && form.phone ? (
                            <div className="alert alert--error">
                                Please enter a valid phone number.
                            </div>
                        ) : null}

                        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                            <button
                                type="button"
                                className="btn btn--secondary"
                                onClick={() => setStep(1)}
                                disabled={submitting}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn btn--primary"
                                disabled={!detailsValid || !selectedTime || submitting}
                                onClick={handleBook}
                            >
                                {submitting ? "Booking..." : "Book Discovery Call"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BookDiscoveryPage;
